import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Tags, Plus, Edit, Trash2, Search, Shield } from 'lucide-react';

const ISCIRCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formName, setFormName] = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/iscir/categories/all');
      setCategories(res.data);
    } catch (err) {
      toast.error('Eroare la incarcarea categoriilor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Introduceti denumirea categoriei');
      return;
    }
    try {
      if (editItem) {
        await api.put(`/iscir/categories/${editItem.id}`, { name: formName.trim() });
        toast.success('Categorie actualizata!');
      } else {
        await api.post('/iscir/categories', { name: formName.trim() });
        toast.success('Categorie adaugata!');
      }
      setShowDialog(false);
      setEditItem(null);
      setFormName('');
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Sigur doriti sa stergeti categoria "${cat.name}"?`)) return;
    try {
      await api.delete(`/iscir/categories/${cat.id}`);
      toast.success('Categorie stearsa!');
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la stergere');
    }
  };

  const openEdit = (cat) => {
    setEditItem(cat);
    setFormName(cat.name);
    setShowDialog(true);
  };

  const openAdd = () => {
    setEditItem(null);
    setFormName('');
    setShowDialog(true);
  };

  const filtered = categories.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 flex items-center gap-2" data-testid="iscir-categories-title">
            <Tags className="w-7 h-7 text-blue-600" />
            Categorii ISCIR Echipamente
          </h1>
          <p className="text-slate-500">Gestionare categorii echipamente supuse reglementarilor ISCIR</p>
        </div>
        <Button onClick={openAdd} className="bg-blue-600 hover:bg-blue-700" data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-2" />Adauga Categorie
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-600" />
                Lista Categorii ({filtered.length})
              </CardTitle>
              <CardDescription>Categoriile sunt folosite la adaugarea verificarilor ISCIR</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Cauta categorie..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-categories"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-slate-400">Se incarca...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400">Nu exista categorii</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map(cat => (
                <div
                  key={cat.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors"
                  data-testid={`category-item-${cat.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <span className="font-medium text-slate-800">{cat.name}</span>
                    {!cat.is_active && <Badge variant="outline" className="text-xs text-slate-400">Inactiv</Badge>}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(cat)}
                      title="Editeaza"
                      data-testid={`edit-category-${cat.id}`}
                    >
                      <Edit className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(cat)}
                      title="Sterge"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-category-${cat.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Editeaza Categorie' : 'Adauga Categorie Noua'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Denumire categorie *</Label>
              <Input
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder="Ex: Liza electrica, Transpalet..."
                onKeyDown={e => e.key === 'Enter' && handleSave()}
                data-testid="category-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Anuleaza</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-category-btn">
              {editItem ? 'Actualizeaza' : 'Adauga'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ISCIRCategoriesPage;
